<script setup lang="ts">
import { useMutation } from '@vue/apollo-composable'
import { useBreakpoints } from '@vueuse/core'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import type { FormData } from '@/types/global'

import InputField from '@/components/forms/fields/InputField.vue'
import { useForm } from '@/composables/useForm.ts'
import { Login as LoginMutation } from '@/graphql/auth/auth.mutations.ts'
import { useAuthStore } from '@/store/auth.ts'
import { useNoticeStore } from '@/store/notice.ts'

const isMobile = useBreakpoints({ sm: 600 }).smaller('sm')

const auth = useAuthStore()
const router = useRouter()
const { t } = useI18n({ useScope: 'global' })

const form = ref<FormData>({
  fields: {
    email: {
      error: null,
      type: 'email',
      value: '',
    },
    password: {
      error: null,
      type: 'password',
      value: '',
    },
  },
  valid: false,
})
const { validate } = useForm(form)
const sending = ref(false)

const { mutate, onDone, onError } = useMutation(LoginMutation)

onDone(async ({ data }) => {
  sending.value = false
  useNoticeStore().set({
    message: t('sign_in_success'),
    ttl: 5_000,
    type: 'success',
  })
  await auth.setUser(data.login, true)
})

onError(() => {
  form.value.fields.email.error = null
  form.value.fields.password.error = 'You have entered an invalid username or password'
  sending.value = false
})

const login = () => {
  if (!form.value.valid || sending.value) return

  sending.value = true
  form.value.fields.email.error = null
  form.value.fields.password.error = null
  mutate({ email: form.value.fields.email.value, password: form.value.fields.password.value })
}
</script>

<template>
  <section>
    <RouterLink v-if="isMobile" class="auth-top-link" :to="{ name: 'signup' }">Sign up</RouterLink>
    <h1 class="mb-100">{{ t('sign_in') }}</h1>

    <div v-if="router.currentRoute.value.query['changed-password']" class="auth-top__success">
      {{ t('password_changed') }}
      <i class="material-icons">done</i>
    </div>

    <form action="" class="auth-block__form" @submit.prevent>
      <InputField
        :error="form.fields.email.error"
        :label="t('email')"
        name="email"
        :type="form.fields.email.type"
        v-model="form.fields.email.value"
        required
        @input="validate('email')"
      />
      <InputField
        :error="form.fields.password.error"
        :label="t('password')"
        name="password"
        :type="form.fields.password.type"
        v-model="form.fields.password.value"
        required
        @input="validate('password')"
      />
      <div class="auth-block__options">
        <div class="checkbox-container">
          <div class="checkbox bounce">
            <input id="remember" type="checkbox" />
            <svg viewBox="0 0 21 21">
              <polyline points="5 10.75 8.5 14.25 16 6"></polyline>
            </svg>
          </div>
          <label for="remember">{{ t('remember_me') }}</label>
        </div>
        <RouterLink :to="{ name: 'forgot-password' }">{{ t('forgot_password') }}</RouterLink>
      </div>
      <div class="auth-block__form__bottom">
        <RouterLink :to="{ name: 'signup' }">{{ t('sign_up') }}</RouterLink>
        <button
          :disabled="!form.valid"
          type="submit"
          class="button button--regular --uppercase"
          @click="login"
        >
          {{ t('sign_in') }}
        </button>
      </div>
    </form>
  </section>
</template>
